<template>
  <suite-app-section>
    <template #header>
      <div class="section-title-group">
        <h2 class="section-title title">{{ t('home.lastActivity') }}</h2>
        <span v-if="servicesActivityStore.unreadCount > 0" class="activity-badge">{{ t('home.updates', {num: servicesActivityStore.unreadCount}) }}</span>
      </div>
    </template>
    <template #header-actions>
      <ion-button fill="clear" class="mark-read-btn" @click="servicesActivityStore.markAllAsRead">{{ t('home.uncheckUpdates') }}</ion-button>
    </template>
    <div class="activity-list">
      <div v-for="activity in servicesActivityStore.lastActivities" :key="activity.id" class="activity-item">
        <template v-if="getServiceById(activity.serviceId)">
          <div v-if="false" class="activity-avatar-box">
            <suite-logo :logo="getServiceById(activity.serviceId)?.logo as Logo" />
          </div>
          <div class="activity-content">
            <h4 class="activity-message">{{ activity.message }}</h4>
            <p class="activity-category">{{ activity.category }}</p>
          </div>
          <div class="activity-meta-right">
            <div class="read-activity-wrapper">
              <button v-if="!activity.read" @click="servicesActivityStore.markAsRead(activity.id)" class="view-action-btn" aria-label="View entry details">
                <i class="fa-regular fa-eye"></i>
              </button>
            </div>
            <span class="activity-time">{{ getRelativeDateString(new Date(activity.timestamp)) }}</span>
          </div>
        </template>
      </div>
    </div>
  </suite-app-section>
</template>

<script setup lang="ts">
import {useLocale} from "@/composables/useLocale.ts";
import {IonButton, IonImg} from "@ionic/vue";
import SuiteAppSection from "@/components/suiteAppSection.vue";
import SuiteLogo from "@/components/SuiteLogo.vue";
import { useServicesActivityStore } from "@/stores/useServicesActivityStore.ts";
import {useServices} from "@/composables/useServices.ts";
import {Logo} from "@/models/settings.ts";

const servicesActivityStore = useServicesActivityStore()
const { getServiceById } = useServices();
const { t, localeTwoLetterCode } = useLocale();

const getRelativeDateString = (date: Date): string => {
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffTime = startOfToday.getTime() - startOfTarget.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  if (diffDays === 1) {
    return t('common.yesterday');
  }

  if (diffDays > 1 && diffDays < 7) {
    return date.toLocaleDateString(localeTwoLetterCode.value, { weekday: 'long' }); // "Tuesday" instead of "Tue"
  }

  return date.toLocaleDateString(localeTwoLetterCode.value, { year: 'numeric', month: 'short', day: 'numeric' });
};

</script>

<style scoped>
.section-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title {
  font-size: 1.2rem;
}
.activity-badge {
  background-color: #fce7f3; /* Light pink background matching design mockup */
  color: #db2777; /* Deep readable status pink text */
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

.activity-list {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  gap: 16px;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-avatar-box {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
}

.activity-content {
  flex-grow: 1;
}

.mark-read-btn {
  --color: var(--font-700);
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: none;
  margin: 0;
}

.activity-message {
  font-size: 0.95rem;
  color: #363131;
  font-weight: 500;
  margin: 0 0 3px 0;
}

.activity-category {
  color: #94a3b8;
  font-size: 0.8rem;
  margin: 0;
}

.activity-meta-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Custom Visual Eye View Toggle Action Action Button */
.view-action-btn {
  background-color: #fce7f3;
  color: #db2777;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  cursor: pointer;
}

.activity-time {
  color: #94a3b8;
  font-size: 0.85rem;
  min-width: 28px;
  width: 5rem;
  text-align: end;
}

.read-activity-wrapper {
  width: 1.8rem;
}
</style>